import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { RefreshCw, AlertCircle, FileText, Send, Trash2, Ban, ChevronRight } from 'lucide-react';
import ohipInvoicesService from '../services/OhipInvoicesService';
import Button from './shared/Button';
import Spinner from './shared/Spinner';
import ConfirmModal from './shared/ConfirmModal';
import Toast from './shared/Toast';
import InvoiceWizard from './InvoiceWizard';
import { formatDateTime } from '../utils/datetime';
import './InvoicesPage.css';

// Admin > Billing > Invoices.
//
// One month at a time, because the month IS the unit an OHIP invoice is raised for. The month selector is
// built from the months that actually have invoices, so it can never offer a month with nothing in it.

const money = (n, ccy) => (n == null ? '—'
    : `${ccy || 'GBP'} ${Number(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const num = (n) => (n == null ? '—' : Number(n).toLocaleString('en-GB'));

const STATUS = {
    DRAFT: 'inv-badge-draft',
    SENT:  'inv-badge-sent',
    PAID:  'inv-badge-paid',
    VOID:  'inv-badge-void',
};

function InvoicesPage() {
    const [months, setMonths] = useState([]);
    const [month, setMonth] = useState('');
    const [status, setStatus] = useState('');
    const [search, setSearch] = useState('');
    // Debounced copy of `search`. The list query keys off THIS, so typing "Rotana" is one request rather
    // than six — the same reason the OHIP Transactions filters debounce.
    const [searchApplied, setSearchApplied] = useState('');
    const [invoices, setInvoices] = useState([]);
    const [expanded, setExpanded] = useState(null);
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [wizard, setWizard] = useState(false);
    const [confirm, setConfirm] = useState(null);
    const [toast, setToast] = useState(null);

    const loadMonths = useCallback(async () => {
        const res = await ohipInvoicesService.months();
        const list = res.data || [];
        setMonths(list);
        // Newest first, so the screen opens on the month someone is most likely to be working on.
        setMonth(m => m || (list[0] ? list[0].invoiceMonth : ''));
        return list;
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = {};
            if (month) params.month = month;
            if (status) params.status = status;
            if (searchApplied.trim()) params.search = searchApplied.trim();
            const res = await ohipInvoicesService.list(params);
            setInvoices(res.data || []);
        } catch (e) {
            setError(e.response?.data?.message || e.message || 'Failed to load invoices');
            setInvoices([]);
        } finally {
            setLoading(false);
        }
    }, [month, status, searchApplied]);

    useEffect(() => {
        const t = setTimeout(() => setSearchApplied(search), 350);
        return () => clearTimeout(t);
    }, [search]);

    useEffect(() => { loadMonths().catch(() => {}); }, [loadMonths]);
    useEffect(() => { load(); }, [load]);

    const openRow = async (inv) => {
        if (expanded === inv.id) { setExpanded(null); setDetail(null); return; }
        setExpanded(inv.id);
        setDetail(null);
        try {
            const res = await ohipInvoicesService.get(inv.id);
            setDetail(res.data);
        } catch (e) {
            setDetail({ lines: [], error: e.response?.data?.message || e.message });
        }
    };

    const act = async (fn, okMessage) => {
        setBusy(true);
        try {
            await fn();
            setToast({ kind: 'success', message: okMessage });
            await load();
            await loadMonths();
        } catch (e) {
            setToast({ kind: 'error', message: e.response?.data?.message || e.message || 'That did not work' });
        } finally {
            setBusy(false);
            setConfirm(null);
        }
    };

    const totals = useMemo(() => {
        const m = months.find(x => x.invoiceMonth === month);
        return m || null;
    }, [months, month]);

    return (
        <div className="otx-page">
            <div className="otx-header">
                <div>
                    <h1>Invoices</h1>
                    <p className="otx-subtitle">
                        OHIP API usage billed per environment, per calendar month. One invoice per environment
                        per month — re-generating rewrites the draft rather than raising a second one.
                    </p>
                </div>
                <div className="otx-header-actions">
                    <Button variant="secondary" onClick={() => setWizard(true)}>
                        <FileText className="icon" /> Invoice all
                    </Button>
                    <Button variant="success" onClick={load}>
                        <RefreshCw className="icon" /> Refresh
                    </Button>
                </div>
            </div>

            <div className="otx-filters">
                <label>
                    <span>Month</span>
                    {/* Built from the months that HAVE invoices — a selector offering an empty month is a
                        dead end the reader has to discover by clicking it. */}
                    <select value={month} onChange={e => setMonth(e.target.value)}>
                        {!months.length && <option value="">No invoices yet</option>}
                        {months.map(m => (
                            <option key={m.invoiceMonth} value={m.invoiceMonth}>
                                {m.invoiceMonth} — {num(m.invoices)} invoice(s)
                            </option>
                        ))}
                    </select>
                </label>
                <label>
                    <span>Status</span>
                    <select value={status} onChange={e => setStatus(e.target.value)}>
                        <option value="">All</option>
                        {['DRAFT', 'SENT', 'PAID', 'VOID'].map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                </label>
                <label className="otx-grow">
                    <span>Search</span>
                    <input type="text" value={search} placeholder="Environment, key or Clear Books id"
                           onChange={e => setSearch(e.target.value)} />
                </label>
            </div>

            {error && <div className="otx-error"><AlertCircle size={16} /> {error}</div>}

            {totals && (
                <div className="otx-stats">
                    <div className="otx-stat">
                        <span className="otx-stat-label">Invoices</span>
                        <span className="otx-stat-value">{num(totals.invoices)}</span>
                    </div>
                    <div className="otx-stat">
                        <span className="otx-stat-label">Draft</span>
                        <span className="otx-stat-value">{num(totals.drafts)}</span>
                    </div>
                    <div className="otx-stat">
                        <span className="otx-stat-label">Sent</span>
                        <span className="otx-stat-value otx-ok">{num(totals.sent)}</span>
                    </div>
                    <div className="otx-stat">
                        <span className="otx-stat-label">Requests</span>
                        <span className="otx-stat-value">{num(totals.totalRequests)}</span>
                    </div>
                    <div className="otx-stat">
                        <span className="otx-stat-label">Value</span>
                        <span className="otx-stat-value">{money(totals.amount, totals.currency)}</span>
                    </div>
                </div>
            )}

            <div className="otx-table-wrap">
                <table className="otx-table">
                    <thead>
                        <tr>
                            <th style={{ width: 34 }} />
                            <th>Environment</th>
                            <th>Period</th>
                            <th className="otx-numeric">Requests</th>
                            <th className="otx-numeric">Rate /1k</th>
                            <th className="otx-numeric">Amount</th>
                            <th>Status</th>
                            <th>Clear Books</th>
                            <th style={{ width: 120 }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan={9}><Spinner /></td></tr>}
                        {!loading && !invoices.length && (
                            <tr><td colSpan={9} className="otx-empty">No invoices for this selection.</td></tr>
                        )}
                        {!loading && invoices.map(inv => (
                            <React.Fragment key={inv.id}>
                                <tr className="inv-row" onClick={() => openRow(inv)}>
                                    <td className="inv-chevron">
                                        <ChevronRight size={15} className={expanded === inv.id ? 'inv-chevron-open' : ''} />
                                    </td>
                                    <td>
                                        {inv.environmentName || inv.environmentGroupName}
                                        <code className="inv-key">{inv.environmentKey}</code>
                                    </td>
                                    <td>{inv.periodStart} → {inv.periodEnd}</td>
                                    <td className="otx-numeric">{num(inv.totalRequests)}</td>
                                    <td className="otx-numeric">{Number(inv.ratePer1000).toFixed(4)}</td>
                                    <td className="otx-numeric">
                                        {money(inv.amount, inv.currency)}
                                        {!!inv.minimumApplied && <span className="inv-badge inv-badge-min">min</span>}
                                    </td>
                                    <td><span className={`inv-badge ${STATUS[inv.status] || ''}`}>{inv.status}</span></td>
                                    <td>
                                        {inv.clearbooksId
                                            ? <code className="inv-key">{inv.clearbooksId}</code>
                                            : inv.clearbooksError
                                                ? <span className="inv-err" title={inv.clearbooksError}>failed</span>
                                                : <span className="otx-muted">—</span>}
                                    </td>
                                    <td onClick={e => e.stopPropagation()}>
                                        <div className="inv-actions">
                                            {inv.status === 'DRAFT' && (
                                                <button className="inv-btn" disabled={busy} title="Send to Clear Books"
                                                        onClick={() => setConfirm({
                                                            kind: 'send', inv,
                                                            title: 'Send to Clear Books',
                                                            message: `Raise ${money(inv.amount, inv.currency)} for ${inv.environmentName || inv.environmentKey} in Clear Books. Once sent, re-generating will no longer change this invoice.`,
                                                        })}>
                                                    <Send size={13} /> Send
                                                </button>
                                            )}
                                            {inv.status !== 'VOID' && (
                                                <button className="inv-btn" disabled={busy} title="Void"
                                                        onClick={() => setConfirm({
                                                            kind: 'void', inv,
                                                            title: 'Void invoice',
                                                            message: 'Mark this invoice VOID. It stays on the screen and in the month total as a voided line — nothing is removed from Clear Books, which has to be voided there too.',
                                                        })}>
                                                    <Ban size={13} />
                                                </button>
                                            )}
                                            {inv.status === 'DRAFT' && (
                                                <button className="inv-btn inv-btn-danger sa-only" disabled={busy} title="Delete draft"
                                                        onClick={() => setConfirm({
                                                            kind: 'delete', inv,
                                                            title: 'Delete draft invoice',
                                                            message: 'Delete this draft. It can be generated again from the same usage at any time.',
                                                        })}>
                                                    <Trash2 size={13} />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                                {expanded === inv.id && (
                                    <tr className="inv-detail-row">
                                        <td colSpan={9}>
                                            {!detail && <Spinner />}
                                            {detail && (
                                                <div className="inv-detail">
                                                    <div className="inv-detail-meta">
                                                        <div><span>Basis</span>{inv.billableBasis === 'SUCCESS' ? 'Successful requests only' : 'All requests'}</div>
                                                        <div><span>Billable</span>{num(inv.billableRequests)}</div>
                                                        <div><span>Generated</span>{inv.createdAt ? formatDateTime(inv.created_at || inv.createdAt) : '—'}{inv.generatedBy ? ` by ${inv.generatedBy}` : ''}</div>
                                                        {inv.sentAt && <div><span>Sent</span>{formatDateTime(inv.sentAt)}</div>}
                                                    </div>
                                                    {inv.clearbooksError && (
                                                        <div className="inv-error-box">
                                                            <strong>Clear Books rejected this invoice</strong>
                                                            <div>{inv.clearbooksError}</div>
                                                        </div>
                                                    )}
                                                    <table className="inv-lines">
                                                        <thead>
                                                            <tr>
                                                                <th>Hotel</th>
                                                                <th className="otx-numeric">Requests</th>
                                                                <th className="otx-numeric">Billable</th>
                                                                <th className="otx-numeric">Amount</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {(detail.lines || []).map(l => (
                                                                <tr key={l.hotelCode}>
                                                                    <td>{l.hotelCode}</td>
                                                                    <td className="otx-numeric">{num(l.totalRequests)}</td>
                                                                    <td className="otx-numeric">{num(l.billableRequests)}</td>
                                                                    <td className="otx-numeric">{money(l.amount, inv.currency)}</td>
                                                                </tr>
                                                            ))}
                                                            {!(detail.lines || []).length && (
                                                                <tr><td colSpan={4} className="otx-empty">No per-hotel breakdown recorded.</td></tr>
                                                            )}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>

            {wizard && (
                <InvoiceWizard
                    onClose={() => setWizard(false)}
                    onDone={() => { load(); loadMonths(); }}
                />
            )}

            {confirm && (
                <ConfirmModal
                    title={confirm.title}
                    message={confirm.message}
                    confirmLabel={confirm.kind === 'delete' ? 'Delete' : confirm.kind === 'void' ? 'Void' : 'Send'}
                    danger={confirm.kind === 'delete'}
                    onCancel={() => setConfirm(null)}
                    onConfirm={() => {
                        if (confirm.kind === 'send')   return act(() => ohipInvoicesService.send(confirm.inv.id), 'Sent to Clear Books');
                        if (confirm.kind === 'void')   return act(() => ohipInvoicesService.update(confirm.inv.id, { status: 'VOID' }), 'Invoice voided');
                        return act(() => ohipInvoicesService.remove(confirm.inv.id), 'Draft deleted');
                    }}
                />
            )}

            {toast && <Toast message={toast.message} kind={toast.kind} onClose={() => setToast(null)} />}
        </div>
    );
}

export default InvoicesPage;
